import { useEffect, useRef } from 'react';

import { useChatStore } from '../useChatStore';
import {
  AGENT_STARTERS,
  pickRandomStarters,
  STARTER_DISPLAY_COUNT,
  startersToChatOptions,
} from './staticStarters';

export const useChatOptions = (
  sendMessage: (message: string) => Promise<void>
) => {
  const {
    messages,
    options: currentOptions,
    setOptions,
    clearOptions,
  } = useChatStore();

  // Keep the latest sendMessage in a ref so a starter's action always calls the
  // current callback. The chips are published once per empty chat (so they don't
  // reshuffle mid-conversation), but `sendMessage` can change afterwards — e.g.
  // the draft-prompt toggle rebinds it — and a click must use the current value,
  // not the one captured when the chips were rendered.
  const sendMessageRef = useRef(sendMessage);
  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    // Starters only show on an empty transcript (alongside the empty-state
    // welcome), so they vanish on the first send.
    if (messages.length > 0) {
      if (currentOptions.length > 0) {
        clearOptions();
      }

      return;
    }

    // Empty transcript with nothing published — a fresh panel open, or "New chat"
    // just cleared the chips. Pick a fresh random subset from the pool and show
    // it. Picking here (rather than once per mount) is what makes the chips
    // reshuffle on every New chat, and it self-heals if the options are ever
    // cleared out from under us. Guarding on an empty store keeps it from
    // re-picking on every render (no loop).
    if (currentOptions.length === 0) {
      setOptions(
        startersToChatOptions(
          pickRandomStarters(AGENT_STARTERS, STARTER_DISPLAY_COUNT),
          (message) => sendMessageRef.current(message),
          'static'
        )
      );
    }
  }, [messages, currentOptions, setOptions, clearOptions]);
};
